import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import {
  AuthenticationResult,
  InteractionRequiredAuthError,
  IPublicClientApplication,
  AccountInfo,
} from "@azure/msal-browser";
import { AxiosRequestConfig, Method } from "axios";
import httpService from "../services/httpService";
import { api_s2 } from "../config";

export type ApiMethod = Method;

export const callPymbleApi = async (
  instance: IPublicClientApplication,
  method: Method,
  url: string,
  options: Omit<AxiosRequestConfig, "method" | "url" | "headers"> | null = null
) => {
  // MSAL v4 requires initialize() before any API call.
  // Idempotent — safe to call even if MsalProvider already initialized.
  await instance.initialize();

  let account: AccountInfo | null = null;

  try {
    const accounts = instance.getAllAccounts();
    account = accounts[0] || null;
  } catch (error) {
    console.error("callPymbleApi - Error getAllAccounts", error);
    return null;
  }

  if (!account) {
    // No account yet — expected before authentication completes.
    return null;
  }

  let response: AuthenticationResult | void;
  try {
    response = await instance.acquireTokenSilent({
      scopes: api_s2.scopes,
      account,
    });
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      console.error("Could not get token silently. Try interactive.");
      await instance.acquireTokenRedirect({
        scopes: api_s2.scopes,
        account,
      });
      return null;
    }
    console.error("acquireTokenSilent failed:", (error as Error).message);
    return null;
  }

  if (!response?.accessToken) {
    console.error("callPymbleApi - No access token found.");
    return null;
  }

  const res = await httpService.request({
    method,
    url,
    headers: {
      Authorization: `Bearer ${response.accessToken}`,
    },
    ...options,
  });

  return res?.data ?? null;
};

/**
 * Hook to fetch api data from groups api.
 * Params:
 *   method: <string>  GET | POST | PUT | DELETE
 *   url: <string>  Url of endpoint
 *   options: Extra options to include in Axios call, eg { data: {...} }
 *            for a POST request
 */
export function usePymbleApi() {
  const { instance } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  const callApi = async (
    method: Method,
    url: string,
    options: Omit<AxiosRequestConfig, "method" | "url" | "headers"> | null = null
  ) => {
    return callPymbleApi(instance, method, url, options);
  };

  return {
    callApi,
    isAuthenticated,
  };
}
